import { useState } from 'react';

import { Badge } from '@/components/core/Badge';
import { StatusMessagePopover } from '@/components/shared/StatusMessagePopover';
import { useDialogHost } from '@/hooks/useDialogHost';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { formatDate } from '@/services/dateFormat';

import { usePublicationResponseErrors } from './executed.publication.queries';
import { PublicationDataType, PublicationResponseError } from './executed.publication.types';

const publicationErrorText = (errors: PublicationResponseError[]) => errors
    .map((error) => error.errorDescription?.trim() || error.errorCode?.trim() || '')
    .filter((text) => text.length > 0)
    .join('\n\n');

interface PublicationErrorStatusProps {
    publication: PublicationDataType;
    /** Storybook-only: open the popover on first render when errors are already cached. */
    initiallyOpen?: boolean;
}

const PublicationErrorStatus = ({
    publication,
    initiallyOpen = false,
}: PublicationErrorStatusProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { openPublicationError } = useDialogHost();
    const query = usePublicationResponseErrors(publication.id);
    const [resolvedErrors, setResolvedErrors] = useState<PublicationResponseError[] | undefined>();
    const errors = query.data ?? resolvedErrors ?? [];
    const message = publicationErrorText(errors);
    const count = errors.length;

    const prepareOpen = async () => {
        if ((query.data?.length ?? 0) > 0) {
            return true;
        }
        if (query.isSuccess) {
            return false;
        }

        const result = await query.refetch();
        if (result.isError) {
            toast.error('PUBLICATION_ERROR.LOAD_FAILED');
            return false;
        }
        const next = result.data ?? [];
        setResolvedErrors(next);
        return next.length > 0;
    };

    const showReport = () => {
        openPublicationError({
            message,
            destination: publication.destinationName ?? '',
            date: formatDate(publication.creationDate, 'mediumDate'),
            errors,
        });
    };

    return (
        <StatusMessagePopover
            badge={(
                <Badge variant="error" className="tw-shrink-0 tw-whitespace-nowrap">
                    {t(publication.taskStatus)}
                </Badge>
            )}
            header={(
                <Badge variant="error" number={count}>
                    {t(count === 1
                        ? 'PUBLICATION_ERROR.ERROR_ONE'
                        : 'PUBLICATION_ERROR.ERROR_OTHER')}
                </Badge>
            )}
            message={message}
            ariaLabel={t('PUBLICATION_ERROR.DETAILS')}
            initiallyOpen={initiallyOpen}
            prepareOpen={prepareOpen}
            onShowReport={showReport}
        />
    );
};

PublicationErrorStatus.displayName = 'PublicationErrorStatus';

export { PublicationErrorStatus };
